(() => {
  const euCountries = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE EU'.split(' '));
  const rhsToUsda = Object.freeze({
    H1a: 13, H1b: 12, H1c: 11, H2: 10, H3: 10, H4: 9, H5: 8, H6: 7, H7: 5
  });

  function countryToSystem(country) {
    const code = String(country || '').toUpperCase();
    if (code === 'US' || code === 'CA' || code === 'GB') return code;
    return euCountries.has(code) ? 'EU' : 'OTHER';
  }

  function toUsda(system, value) {
    const selected = String(value);
    if (system === 'CA') {
      const match = /^([0-9])[ab]$/i.exec(selected);
      return match ? Number(match[1]) + 1 : null;
    }
    if (system === 'GB') return rhsToUsda[selected] ?? null;
    const number = Number(selected);
    return Number.isInteger(number) && number >= 3 && number <= 10 ? number : null;
  }

  const api = { countryToSystem, toUsda };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;

  const root = document.querySelector('[data-hg-zone-picker]');
  if (!root) return;
  const system = countryToSystem(root.dataset.country);
  const dialog = root.querySelector('dialog');
  const opener = root.querySelector('[data-hg-zone-open]');
  const choices = [...root.querySelectorAll('[data-hg-zone-value]')];
  const help = root.querySelector('#hg-zone-help');
  const zip = root.querySelector('#hg-zone-zip');
  const translated = root.dataset;
  const storageKey = `hg-zone-${system}`;
  const defaultValue = system === 'CA' ? '6b' : system === 'GB' ? 'H4' : '7';
  const withZone = (template, value) => template.replace('__ZONE__', String(value));
  let selected = defaultValue;
  let returnFocus = null;

  try {
    const saved = localStorage.getItem(storageKey) ?? (system === 'US' ? localStorage.getItem('hg-zone') : null);
    if (saved !== null && toUsda(system, saved) !== null && choices.some((button) => button.dataset.hgZoneValue === saved)) selected = saved;
  } catch (_) { /* Storage can be unavailable in private browsing. */ }

  function displayLabel(value) {
    const template = system === 'CA' ? translated.caZoneLabel
      : system === 'GB' ? translated.rhsZoneLabel
        : system === 'EU' ? translated.euZoneLabel : translated.zoneLabel;
    return withZone(template, value);
  }

  function sync(emit = false) {
    const zone = toUsda(system, selected);
    const zoneLabel = displayLabel(selected);
    document.documentElement.dataset.zone = String(zone);
    document.documentElement.dataset.zoneLabel = zoneLabel;
    document.querySelectorAll('[data-hg-zone-label]').forEach((node) => { node.textContent = zoneLabel; });
    document.querySelectorAll('[data-hg-zone-open]').forEach((node) => {
      node.setAttribute('aria-label', withZone(translated.currentMarketZoneLabel, zoneLabel));
    });
    choices.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.hgZoneValue === selected)));
    // Point every "Plant This Week" link at the collection for this zone band (cold 3–5, mild 6–8, warm 9–10).
    const band = zone == null ? 'mild' : zone <= 5 ? 'cold' : zone >= 9 ? 'warm' : 'mild';
    document.querySelectorAll('a[href*="/collections/plant-now-"]').forEach((link) => {
      link.href = link.href.replace(/plant-now-(cold|mild|warm)/, `plant-now-${band}`);
    });
    if (emit) document.dispatchEvent(new CustomEvent('hg:zone-change', { detail: { zone, zoneLabel } }));
  }

  function close() { if (dialog.open) dialog.close(); }
  function choose(value) {
    const next = String(value);
    if (toUsda(system, next) === null || !choices.some((button) => button.dataset.hgZoneValue === next)) return;
    selected = next;
    try { localStorage.setItem(storageKey, selected); } catch (_) { /* Session choice still works. */ }
    sync(true);
    close();
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-hg-zone-open]');
    if (trigger) {
      returnFocus = trigger;
      if (!dialog.open) { dialog.showModal(); dialog.querySelector('[data-hg-zone-close]').focus(); }
      return;
    }
    if (event.target.closest('[data-hg-zone-close]')) close();
    const choice = event.target.closest('[data-hg-zone-value]');
    if (choice) choose(choice.dataset.hgZoneValue);
  });
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('close', () => { if (returnFocus?.isConnected) returnFocus.focus(); returnFocus = null; });
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const items = [...dialog.querySelectorAll('button:not([disabled]), input:not([disabled])')];
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  root.querySelector('[data-hg-zone-form]')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const value = zip.value.trim();
    if (!/^\d{5}$/.test(value)) { help.textContent = translated.zipError; zip.focus(); return; }
    const estimate = Math.max(3, Math.min(10, Number(value[0]) + 2));
    help.textContent = withZone(translated.zipEstimate, estimate);
    choose(estimate);
  });

  function populateDrawer() {
    const menu = document.querySelector('#menu-drawer .menu-drawer__menu');
    if (!menu || menu.querySelector('[data-hg-zone-open]')) return;
    // The main menu usually carries Plant This Week already; only add it when missing, pointed at the zone's collection.
    if (!menu.querySelector('a[href*="/collections/plant-now"]')) {
      const usda = Number(document.documentElement.dataset.zone);
      const band = !usda ? 'mild' : usda <= 5 ? 'cold' : usda >= 9 ? 'warm' : 'mild';
      const plant = document.createElement('li');
      plant.className = 'hg-drawer-plant';
      const plantLink = document.createElement('a');
      plantLink.href = document.querySelector('.hg-plant-nav a')?.href || `/collections/plant-now-${band}`;
      plantLink.className = 'menu-drawer__menu-item list-menu__item link link--text focus-inset';
      plantLink.textContent = translated.plantThisWeek;
      plant.append(plantLink);
      menu.prepend(plant);
    }
    const item = document.createElement('li');
    item.className = 'hg-drawer-zone';
    item.append(opener.cloneNode(true));
    const plantItem = menu.querySelector('.hg-drawer-plant');
    if (plantItem) menu.insertBefore(item, plantItem.nextSibling); else menu.prepend(item);
  }
  populateDrawer();
  document.addEventListener('shopify:section:load', populateDrawer);
  sync(true);
})();
